import React, { useState, useMemo, useEffect } from "react"
import { Dropdown, Input, Button, Space, Checkbox } from "antd"
import { MoreOutlined, SortAscendingOutlined, SortDescendingOutlined, ClearOutlined, SearchOutlined, CloseSquareOutlined } from "@ant-design/icons"

const TanstackColumnDropDown = ({ column, table }) => {
  const [searchText, setSearchText] = useState("")
  const [open, setOpen] = useState(false)
  const [selectedValues, setSelectedValues] = useState(column.columnDef.meta?.isNumber ? { type: "eq", number: "" } : [])
  const formatValue = column?.columnDef?.meta?.formatValue || ((v) => v?.toString() || "")

  // -------------------------------------------------------------
  // UNIQUE VALUES
  // -------------------------------------------------------------
  // const uniqueValues = useMemo(() => {
  //   const set = new Set()
  //   table.getPreFilteredRowModel().flatRows.forEach((row) => {
  //     const val = row.getValue(column.id)
  //     if (val !== undefined && val !== null && val !== "") set.add(formatValue(val))
  //   })
  //   return Array.from(set).sort((a, b) => a?.toString().localeCompare(b?.toString()))
  // }, [
  //   table
  //     .getPreFilteredRowModel()
  //     .flatRows.map((r) => r.id)
  //     .join("|"),
  //   column.id,
  // ])

  const uniqueValues = useMemo(() => {
    const faceted = column.getFacetedUniqueValues?.()
    if (!faceted) return []

    return Array.from(faceted.keys())
      .filter((v) => v !== undefined && v !== null && v !== "")
      .map((v) => formatValue(v))
      .sort((a, b) => a.toString().localeCompare(b.toString()))
  }, [
    column.id,
    table.getPreFilteredRowModel().rows.length, // <-- stable & updates list
  ])

  // -------------------------------------------------------------
  // FILTERED VALUES (after search)
  // -------------------------------------------------------------
  const filteredValues = useMemo(() => uniqueValues.filter((v) => v?.toString().toLowerCase().includes(searchText.toLowerCase())), [uniqueValues, searchText])
  // -------------------------------------------------------------
  // SYNC selection with external filters (if already applied)
  // -------------------------------------------------------------
  // useEffect(() => {
  //   const fv = column.getFilterValue()
  //   if (Array.isArray(fv)) setSelectedValues(fv)
  //   else setSelectedValues([])
  // }, [column.getFilterValue()])

  useEffect(() => {
    const fv = column.getFilterValue?.()
    if (column.columnDef.meta?.isNumber) {
      setSelectedValues(fv && typeof fv === "object" ? fv : { type: "eq", number: "" })
    } else {
      setSelectedValues(Array.isArray(fv) ? fv : [])
    }
  }, [column.id, column.columnDef.meta?.isNumber])
  // -------------------------------------------------------------
  // APPLY FILTER TO TANSTACK
  // -------------------------------------------------------------
  // const applyFilter = (values) => {
  //   if (values.length > 0) column.setFilterValue(values)
  //   else column.setFilterValue(undefined)
  // }

  const applyFilter = (values) => {
    if (Array.isArray(values)) {
      column.setFilterValue(values.length > 0 ? values : undefined)
    } else if (values && typeof values === "object") {
      column.setFilterValue(values.number !== "" ? values : undefined)
    } else {
      column.setFilterValue(undefined)
    }
  }

  // const handleCheck = (value, checked) => {
  //   const updated = checked ? [...selectedValues, value] : selectedValues.filter((v) => v !== value)

  //   setSelectedValues(updated)
  //   applyFilter(updated)
  // }

  // const handleCheck = (value, checked) => {
  //   if (!Array.isArray(selectedValues)) return // skip if number filter
  //   const updated = checked ? [...selectedValues, value] : selectedValues.filter((v) => v !== value)
  //   setSelectedValues(updated)
  //   applyFilter(updated)
  // }

  const handleCheck = (value, checked) => {
    if (!Array.isArray(selectedValues)) return // skip for number columns

    const updated = checked ? [...selectedValues, value] : selectedValues.filter((v) => v !== value)
    setSelectedValues(updated)
    applyFilter(updated)
  }

  // const handleSelectAll = (checked) => {
  //   if (checked) {
  //     // Select ONLY the currently visible (searched) values
  //     setSelectedValues(filteredValues)
  //     applyFilter(filteredValues)
  //   } else {
  //     setSelectedValues([])
  //     applyFilter([])
  //   }
  // }

  const handleSelectAll = (checked) => {
    if (!Array.isArray(selectedValues)) return
    const updated = checked ? filteredValues : []
    setSelectedValues(updated)
    applyFilter(updated)
  }

  const clearFilter = () => {
    setSelectedValues([])
    column.setFilterValue(undefined)
    setSearchText("")
  }

  const isNumber = column.columnDef.meta?.isNumber
  // const isSelected = !isNumber && Array.isArray(selectedValues) ? selectedValues.includes(item) : false

  // -------------------------------------------------------------
  // DROPDOWN UI
  // -------------------------------------------------------------
  let menu = (
    <div style={{ padding: 8, width: 260, background: "var(--va-card-1)" }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        {/* Sorting */}
        <Button icon={<SortAscendingOutlined />} onClick={() => column.toggleSorting(false)} block>
          Sort Ascending
        </Button>
        <Button icon={<SortDescendingOutlined />} onClick={() => column.toggleSorting(true)} block>
          Sort Descending
        </Button>
        <Button icon={<ClearOutlined />} onClick={() => column.clearSorting()} block>
          Clear Sort
        </Button>
        <Button
          icon={<CloseSquareOutlined />}
          onClick={() => {
            column.toggleVisibility(false)
          }}
          block
        >
          Hide Column
        </Button>

        <div style={{ borderTop: "1px solid #eee", margin: "10px 0" }} />

        {/* Search box */}
        <Input placeholder={`Search ${column?.columnDef?.header}`} prefix={<SearchOutlined />} value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear size="small" />

        {/* SELECT ALL */}
        <div style={{ marginTop: 6 }}>
          <Checkbox
            indeterminate={selectedValues.length > 0 && selectedValues.length < filteredValues.length}
            checked={filteredValues.length > 0 && selectedValues.length === filteredValues.length}
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            <b>Select All</b>
          </Checkbox>
        </div>

        {/* Multi Select List */}
        <div
          style={{
            maxHeight: 160,
            overflowY: "auto",
            border: "1px solid #eee",
            borderRadius: 4,
            padding: 6,
            background: "var(--va-card-2)",
          }}
        >
          {filteredValues.map((v) => (
            <div key={v}>
              <Checkbox checked={!column.columnDef.meta?.isNumber && Array.isArray(selectedValues) && selectedValues.includes(v)} onChange={(e) => handleCheck(v, e.target.checked)}>
                {v}
              </Checkbox>
            </div>
          ))}

          {filteredValues.length === 0 && <div style={{ color: "#888", textAlign: "center", padding: 4 }}>No matches</div>}
        </div>

        {/* Clear Filter Button */}
        <Button onClick={clearFilter} block icon={<ClearOutlined />} danger size="small">
          Clear Filter
        </Button>
      </Space>
    </div>
  )

  if (column.columnDef.meta?.isNumber) {
    menu = (
      <div style={{ padding: 8, width: 260, background: "var(--va-card-1)" }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {/* Sorting */}
          <Button icon={<SortAscendingOutlined />} onClick={() => column.toggleSorting(false)} block>
            Sort Ascending
          </Button>
          <Button icon={<SortDescendingOutlined />} onClick={() => column.toggleSorting(true)} block>
            Sort Descending
          </Button>
          <Button icon={<ClearOutlined />} onClick={() => column.clearSorting()} block>
            Clear Sort
          </Button>

          <div style={{ padding: 8, width: 240 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <select value={selectedValues.type} onChange={(e) => setSelectedValues((old) => ({ ...old, type: e.target.value }))}>
                <option value="eq">= Equal</option>
                <option value="gt">{">"} Greater Than</option>
                <option value="lt">{"<"} Less Than</option>
                <option value="gte">{">="} Greater/Equal</option>
                <option value="lte">{"<="} Less/Equal</option>
              </select>

              <Input type="number" value={selectedValues.number} onChange={(e) => setSelectedValues((old) => ({ ...old, number: e.target.value }))} placeholder="Enter number" />

              {/* <Button type="primary" block onClick={() => column.setFilterValue(selectedValues)}>
                Apply
              </Button> */}

              <Button type="primary" block onClick={() => applyFilter(selectedValues)}>
                Apply
              </Button>
              <Button danger block onClick={() => column.setFilterValue(undefined)}>
                Clear
              </Button>
            </Space>
          </div>

          <div style={{ borderTop: "1px solid #eee", margin: "10px 0" }} />

          {/* Search box */}
          <Input placeholder={`Search ${column?.columnDef?.header}`} prefix={<SearchOutlined />} value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear size="small" />

          {/* SELECT ALL */}
          <div style={{ marginTop: 6 }}>
            <Checkbox
              indeterminate={selectedValues.length > 0 && selectedValues.length < filteredValues.length}
              checked={filteredValues.length > 0 && selectedValues.length === filteredValues.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              <b>Select All</b>
            </Checkbox>
          </div>

          {/* Multi Select List */}
          <div
            style={{
              maxHeight: 160,
              overflowY: "auto",
              border: "1px solid #eee",
              borderRadius: 4,
              padding: 6,
              background: "var(--va-card-2)",
            }}
          >
            {filteredValues.map((v) => (
              <div key={v}>
                <Checkbox checked={!column.columnDef.meta?.isNumber && Array.isArray(selectedValues) && selectedValues.includes(v)} onChange={(e) => handleCheck(v, e.target.checked)}>
                  {v}
                </Checkbox>
              </div>
            ))}

            {filteredValues.length === 0 && <div style={{ color: "#888", textAlign: "center", padding: 4 }}>No matches</div>}
          </div>

          {/* Clear Filter Button */}
          <Button onClick={clearFilter} block icon={<ClearOutlined />} danger size="small">
            Clear Filter
          </Button>
        </Space>
      </div>
    )
  }

  return (
    <Dropdown popupRender={() => menu} trigger={["click"]} open={open} onOpenChange={setOpen} placement="bottomRight">
      <Button icon={<MoreOutlined />} type={column.getIsFiltered() ? "primary" : "default"} size="small" />
    </Dropdown>
  )
}

export default TanstackColumnDropDown
